import { useContext, useDeferredValue, useEffect, useState } from "react";

import { syntenyViewerDefaultTracks } from "../../../settings.ts";
import { useGenomeViewerStore } from "../../../store/store.ts";
import type { SubType } from "../../../types.ts";
import { TrackConfigurationContext } from "../TrackConfigurationContext.ts";

export const useTrackPanel = () => {
  const [selectedTracks, setSelectedTracks] = useState<string[]>([]);
  const [selectedNuFeatures, setSelectedNuFeatures] = useState<SubType[]>([]);

  const [filterTerm, setFilterTerm] = useState<string>();
  const debouncedFilterText = useDeferredValue(filterTerm);
  const { selectedGroupIds, availableTrackConfiguration, trackConfiguration } = useContext(TrackConfigurationContext);
  const trackGroups = useGenomeViewerStore((state) => state.trackGroups);

  const filteredTracks = syntenyViewerDefaultTracks.filter((track) =>
    debouncedFilterText != undefined ? track.name.toLowerCase().includes(debouncedFilterText.toLowerCase()) : true,
  );

  const filteredNuFeatureTracks: SubType[] = Array.from(
    selectedGroupIds
      .flatMap(
        (groupId) =>
          availableTrackConfiguration
            .get(groupId)
            ?.nuFeatureTypes.filter((track) =>
              debouncedFilterText != undefined
                ? track.name.toLowerCase().includes(debouncedFilterText.toLowerCase())
                : true,
            ) ?? [],
      )
      .reduce((map, track) => {
        if (!map.has(track.id)) {
          map.set(track.id, track);
        }
        return map;
      }, new Map())
      .values(),
  );

  useEffect(() => {
    const newSelectedTracks: string[] = [];
    const newSelectedNuFeatures: SubType[] = [];

    for (const [groupId, trackFeatures] of trackConfiguration) {
      for (const trackType of trackFeatures.defaultTypes) {
        if (!newSelectedTracks.includes(trackType) && selectedGroupIds.includes(groupId)) {
          newSelectedTracks.push(trackType);
        }
      }

      for (const featureType of trackFeatures.nuFeatureTypes) {
        if (!newSelectedNuFeatures.some((track) => track.id === featureType.id) && selectedGroupIds.includes(groupId)) {
          newSelectedNuFeatures.push(featureType);
        }
      }
    }
    setSelectedTracks(newSelectedTracks);
    setSelectedNuFeatures(newSelectedNuFeatures);
  }, [trackConfiguration, selectedGroupIds]);

  return {
    selectedTracks,
    filteredTracks,
    selectedNuFeatures,
    filteredNuFeatureTracks,
    trackGroups,
    setFilterTerm,
  };
};
